import { initialTemperatureControl } from '@grc/ui-shared/lib/components/common/constants/constants';
import {
  Country,
  Shape,
  VolumeUnit
} from '@grc/ui-shared/lib/components/models/enums/enums';
import {
  IFormat,
  IHandlingUnit,
  IHandlingUnitTypes,
  IRegionType
} from '../models/create-orders/create-orders.interface';
import { IOrderViewModel } from '../models/create-orders/order.interface';
import { v4 as uuidv4 } from 'uuid';
import { LoadRestrictions, Orientation } from '../enums/create-order-enum';
import { PhoneType } from '../enums/loads.enum';
import { IOrderBolViewModel } from '../models/create-orders/order-bol.interface';

export const formatData: IFormat[] = [
  {
    id: 1,
    name: 'BOL'
  },
  {
    id: 2,
    name: 'Non-BOL'
  }
];

export const domesticData: IRegionType[] = [
  {
    id: 0,
    name: 'Domestic'
  },
  {
    id: 1,
    name: 'International'
  }
];

export const initialHandlingUnitState: IHandlingUnit = {
  handlingUnitId: uuidv4(),
  handlingUnitTypeId: '',
  handlingUnitType: '',
  quantity: 0,
  dimensions: {
    shape: 1,
    length: null,
    width: null,
    height: null,
    diameter: null
  },
  weight: {
    Unit: 'MassUnit.Pound',
    Value: 0
  },
  nmfcCode: '',
  nmfcClassName: '',
  nmfcId: '',
  productDescription: '',
  hasHazmat: false,
  isNonStackable: false,
  hasTemperatureControl: false,
  temperatureControl: initialTemperatureControl
};

export const initialProduct = {
  productId: uuidv4(),
  productName: '',
  productNumber: '',
  quantity: 0,
  productWeight: {
    Unit: '',
    Value: 0
  },
  innerPackCode: '',
  outerPackCode: '',
  productDescription: '',
  dimensions: {
    shape: 1,
    length: null,
    width: null,
    height: null,
    diameter: null
  }
};

export const initialReferenceState = {
  referenceId: uuidv4(),
  referenceTypeId: '',
  referenceType: '',
  referenceNumber: '',
  isReferenceNumberRequired: false,
  documentRequired: false,
  document: {
    documentId: '',
    documentContent: '',
    documentType: '',
    documentName: ''
  }
};

export const initialOrderState: IOrderViewModel = {
  account: {
    id: '',
    name: ''
  },
  format: { id: 2, name: 'Non-BOL' },
  supplyChainSchedule: '',
  regionType: 'Domestic',
  fetching: false,
  shipperCareOfDetails: {
    id: '',
    firstName: '',
    lastName: '',
    contactType: 'Mobile',
    mobile: '+1 ',
    position: '',
    email: '',
    phoneNumber: {
      phoneType: PhoneType.Mobile,
      phoneNumber: '',
      displayOrder: 1
    }
  },
  consigneeCareOfDetails: {
    id: '',
    firstName: '',
    lastName: '',
    contactType: 'Mobile',
    mobile: '+1 ',
    position: '',
    email: '',
    phoneNumber: {
      phoneType: PhoneType.Mobile,
      phoneNumber: '',
      displayOrder: 1
    }
  },
  handlingUnits: [initialHandlingUnitState],
  products: [initialProduct],
  references: [initialReferenceState],
  isEmailUpdateChecked: false,
  emailDetails: [{ displayOrder: 1, email: '' }],
  isMobileUpdateChecked: false,
  phoneDetails: [
    {
      phoneType: PhoneType.Mobile,
      phoneNumber: '+1 ',
      displayOrder: 1
    }
  ],
  originContacts: [],
  destinationContacts: [],
  lanes: [],
  selectedLaneId: null,
  departmentId: undefined,
  customerServiceContact: undefined,
  resourceLocations: undefined,
  resourceContactId: undefined,
  tenantDefaultContact: undefined,
  searchedAccessorial: '',
  serviceLevelType: 'Standard'
};

export const initialBolOrderState: IOrderBolViewModel = {
  fetching: false,
  shipper: undefined,
  consignee: undefined,
  originContacts: [],
  destinationContacts: [],
  shipperCareOfDetails: {
    id: '',
    firstName: '',
    lastName: '',
    contactType: 'Mobile',
    mobile: '',
    position: '',
    email: '',
    phoneNumber: {
      phoneType: PhoneType.Mobile,
      phoneNumber: '',
      displayOrder: 1
    }
  },
  consigneeCareOfDetails: {
    id: '',
    firstName: '',
    lastName: '',
    contactType: 'Mobile',
    mobile: '',
    position: '',
    email: '',
    phoneNumber: {
      phoneType: PhoneType.Mobile,
      phoneNumber: '',
      displayOrder: 1
    }
  },
  shipperDefaultContactDetails: undefined,
  consigneeDefaultContactDetails: undefined,
  readyPickupDate: undefined,
  closePickupDate: undefined,
  readyDeliveryDate: undefined,
  closeDeliveryDate: undefined,
  invoiceDetails: undefined,
  handlingUnits: [initialHandlingUnitState],
  products: [initialProduct],
  references: [initialReferenceState],
  isEmailUpdateChecked: false,
  emailDetails: [{ displayOrder: 1, email: '' }],
  isMobileUpdateChecked: false,
  phoneDetails: [
    {
      phoneType: PhoneType.Mobile,
      phoneNumber: '+1 ',
      displayOrder: 1
    }
  ],
  value: 0,
  unit: 0,
  trailerLoaded: {
    byDriver: false,
    byShipper: false
  },
  freightCounted: {
    byShipper: false,
    byDriverPallets: false,
    byDriverPlaces: false
  },
  shipperSignature: undefined,
  thirdParty: undefined,
  carrierSignature: undefined,
  carrierPickupDate: undefined,
  codDetails: {
    codAmount: 0,
    feeCollect: {
      collect: false,
      prePaid: false,
      customerCheck: false
    }
  },
  specialInstruction: undefined,
  billOfLading: '',
  carrierDetails: {
    carrierDetails: undefined,
    trailerNumber: '',
    scacNO: '',
    proNO: '',
    sealNo: ''
  },
  carriers: [],
  shipperAddress: {
    streetAddress: [],
    city: '',
    provinceState: '',
    postalZipCode: '',
    country: Country.UnitedStates
  },
  consigneeAddress: {
    streetAddress: [],
    city: '',
    provinceState: '',
    postalZipCode: '',
    country: Country.UnitedStates
  }
};

export const cargoFreqObj: any = {
  name: 'Cargo',
  description:
    'Streamline your logistics operations with CargoLink, the ultimate platform for managing your cargo shipments efficiently.'
};

export const logisticsFreqObj: any = {
  name: 'Logistics',
  description:
    'Revolutionize your logistics management with LogiTrax, a comprehensive solution designed to simplify the complexities of cargo handling. '
};

export const careOfDetailPhoneTypes = [
  { id: PhoneType.Mobile, name: 'Mobile' },
  { id: PhoneType.Home, name: 'Home' },
  { id: PhoneType.Work, name: 'Work' },
  { id: PhoneType.Fax, name: 'Fax' },
  { id: PhoneType.Other, name: 'Other' }
];

export const mostFreqHUD = [];

export const initialCreateHandlingUnit: IHandlingUnitTypes = {
  id: '',
  name: '',
  ediDescriptionCode: '',
  loadRestrictionName: LoadRestrictions.Unknown,
  orientation: Orientation.None,
  density: 0,
  dimensions: {
    shape: Shape.None,
    length: null,
    width: null,
    height: null,
    diameter: null
  },
  cubicFeet: {
    volumeUnit: VolumeUnit.CubicFeet,
    value: 0
  },
  maxStackQty: 0,
  weight: {
    Unit: '',
    Value: 0
  },
  nmfcId: undefined,
  isActive: true,
  isDefault: false
};

export const palletFreqObj: any = {
  name: 'Pallets',
  id: '018de415-b1e6-4221-b385-cb1e0e807076',
  length: 40,
  width: 40,
  height: 40
};

export const boxesFreqObj: any = {
  name: 'Cartons',
  id: '018de416-9619-499c-a93c-de2757e1dc2f',
  length: 40,
  width: 30,
  height: 30
};

export const mostFreqObj: any = {
  'Pallets - 40Lx40Wx40H': palletFreqObj,
  'Cartons - 40Lx30Wx30H': boxesFreqObj
};

export const serviceLevelsType: any = [
  {
    id: 0,
    name: 'Standard'
  },
  {
    id: 1,
    name: 'Expedite'
  }
];
export const telephoneType = ['Mobile', 'Home', 'Work', 'Fax', 'Other'];
export const mostFreqProd = [];
export const mostFreqAccess = [];
